import React, { useState, useRef } from 'react';
import { motion, AnimatePresence, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';

interface University {
  id: number;
  name: string;
  location: string;
  programs: string[];
  ranking: number;
  imageUrl: string;
  description: string;
  stats: {
    students: number;
    international: number;
    programs: number;
  };
}

const Universities: React.FC = () => {
  const [selectedUniversity, setSelectedUniversity] = useState<University | null>(null);
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);
  const controls = useAnimation();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const universities: University[] = [
    {
      id: 1,
      name: 'University of Tokyo',
      location: 'Tokyo',
      programs: ['Engineering', 'Science', 'Liberal Arts', 'Medicine'],
      ranking: 1,
      imageUrl: '../../../images/universities/tokyo.jpeg',
      description: 'Japan\'s most prestigious university, known for its cutting-edge research and academic excellence.',
      stats: {
        students: 28000,
        international: 3500,
        programs: 150
      }
    },
    {
      id: 2,
      name: 'Kyoto University',
      location: 'Kyoto',
      programs: ['Economics', 'Law', 'Engineering', 'Arts'],
      ranking: 2,
      imageUrl: '../../../images/universities/kyoto.jpg',
      description: 'A historic institution combining traditional values with modern research capabilities.',
      stats: {
        students: 23000,
        international: 2800,
        programs: 120
      }
    },
    {
      id: 3,
      name: 'Osaka University',
      location: 'Osaka',
      programs: ['Medicine', 'Science', 'Engineering', 'Liberal Arts'],
      ranking: 3,
      imageUrl: '../../../images/universities/osaka.jpg',
      description: 'Leading research university with strong focus on innovation and international collaboration.',
      stats: {
        students: 25000,
        international: 3000,
        programs: 130
      }
    },
    {
      id: 4,
      name: 'Tohoku University',
      location: 'Sendai',
      programs: ['Technology', 'Science', 'Agriculture', 'Medicine'],
      ranking: 4,
      imageUrl: '../../../images/universities/tohoku.jpg',
      description: 'Pioneering research institution with strong emphasis on practical applications.',
      stats: {
        students: 18000,
        international: 2200,
        programs: 100
      }
    },
    {
      id: 5,
      name: 'Nagoya University',
      location: 'Nagoya',
      programs: ['Engineering', 'Science', 'Medicine', 'Law'],
      ranking: 5,
      imageUrl: '../../../images/universities/nagoya.jpg',
      description: 'Innovative university known for its research in advanced materials and life sciences.',
      stats: {
        students: 16000,
        international: 2000,
        programs: 90
      }
    },
    {
      id: 6,
      name: 'Tokyo Institute of Technology',
      location: 'Tokyo',
      programs: ['Engineering', 'Computer Science', 'Physics', 'Chemistry'],
      ranking: 6,
      imageUrl: '../../../images/universities/tokyo institute.png',
      description: 'Japan\'s leading science and technology university, driving innovation in STEM fields.',
      stats: {
        students: 10000,
        international: 1500,
        programs: 80
      }
    }
  ];

  React.useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);

  return (
    <div className="pt-24 pb-16 mt-10 min-h-screen bg-gradient-to-b from-blue-50 via-white to-pink-50">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary relative inline-block">
            Top Universities in Japan
            <motion.div
              className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-primary to-secondary"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ delay: 0.5, duration: 0.8 }}
            ></motion.div>
          </h1>
          <p className="text-lg text-gray-700 max-w-3xl mx-auto">
            Explore Japan's most prestigious universities offering world-class education and research opportunities.
          </p>
        </motion.div>

        <motion.div 
          ref={ref}
          variants={{
            hidden: { opacity: 0 },
            visible: {
              opacity: 1,
              transition: {
                staggerChildren: 0.1
              }
            }
          }}
          initial="hidden"
          animate={controls}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {universities.map((university, index) => (
            <motion.div
              key={university.id}
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: { opacity: 1, y: 0 }
              }}
              className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300"
              onMouseEnter={() => setHoveredCard(university.id)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={university.imageUrl} 
                  alt={university.name} 
                  className="w-full h-full object-cover transition-transform duration-700 ease-in-out hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
                {/* <div className="absolute top-4 right-4">
                  <span className="px-3 py-1 bg-gradient-to-r from-primary to-secondary text-white text-sm font-medium rounded-full">
                    Rank #{university.ranking}
                  </span>
                </div> */}
              </div>
              <div className="p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-2">{university.name}</h2>
                <p className="text-gray-600 mb-4">{university.location}</p>
                <p className="text-gray-600 text-sm mb-4">{university.description}</p>
                
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary">{university.stats.students.toLocaleString()}</div>
                    <div className="text-xs text-gray-500">Students</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary">{university.stats.international.toLocaleString()}</div>
                    <div className="text-xs text-gray-500">International</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary">{university.stats.programs}</div>
                    <div className="text-xs text-gray-500">Programs</div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-6">
                  {university.programs.map((program) => (
                    <span 
                      key={program} 
                      className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
                    >
                      {program}
                    </span>
                  ))}
                </div>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full px-4 py-3 rounded-lg bg-gradient-to-r from-primary to-secondary text-white font-medium hover:shadow-lg hover:shadow-primary/20 transition-all duration-300"
                  onClick={() => setSelectedUniversity(university)}
                >
                  Learn More
                </motion.button>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.8 }}
          className="mt-16 bg-white rounded-2xl p-8 shadow-lg text-center"
        >
          <h2 className="text-2xl font-semibold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary">
            Ready to Apply?
          </h2>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Our experienced consultants can guide you through the university application process and help you make informed decisions about your educational future in Japan.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-6 py-3 rounded-lg bg-gradient-to-r from-primary to-secondary text-white font-semibold hover:shadow-lg hover:shadow-primary/20 transition-all duration-300"
              >
                Contact Us
              </motion.button>
            </Link>
            <Link to="/programs">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-6 py-3 rounded-lg bg-white text-primary font-semibold border-2 border-primary hover:bg-primary/5 transition-all duration-300"
              >
                Explore Programs
              </motion.button>
            </Link>
          </div>
        </motion.div> */}
      </div>

      {/* University Details Modal */}
      <AnimatePresence>
        {selectedUniversity && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-md"
            onClick={() => setSelectedUniversity(null)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="relative max-w-4xl w-full rounded-2xl overflow-hidden bg-white shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex flex-col md:flex-row h-full">
                <div className="md:w-1/2 h-[300px] md:h-[500px] relative">
                  <img 
                    src={selectedUniversity.imageUrl} 
                    alt={selectedUniversity.name} 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="md:w-1/2 p-6 md:p-8 flex flex-col">
                  <div className="flex items-center justify-between mb-4">
                    <span className="px-3 py-1 bg-gradient-to-r from-primary to-secondary text-white text-sm font-medium rounded-full">
                      Rank #{selectedUniversity.ranking}
                    </span>
                    <button
                      className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200 transition-colors"
                      onClick={() => setSelectedUniversity(null)}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-2">{selectedUniversity.name}</h3>
                  <p className="text-gray-600 mb-4">{selectedUniversity.location}</p>
                  <p className="text-gray-600 mb-6">{selectedUniversity.description}</p>
                  
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-primary">{selectedUniversity.stats.students.toLocaleString()}</div>
                      <div className="text-xs text-gray-500">Students</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-primary">{selectedUniversity.stats.international.toLocaleString()}</div>
                      <div className="text-xs text-gray-500">International</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-primary">{selectedUniversity.stats.programs}</div>
                      <div className="text-xs text-gray-500">Programs</div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-6">
                    {selectedUniversity.programs.map((program) => (
                      <span 
                        key={program} 
                        className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
                      >
                        {program}
                      </span>
                    ))}
                  </div>

                  <div className="mt-auto">
                    <Link to="/contact">
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="w-full px-4 py-3 rounded-lg bg-gradient-to-r from-primary to-secondary text-white font-medium hover:shadow-lg hover:shadow-primary/20 transition-all duration-300"
                      >
                        Apply Now
                      </motion.button>
                    </Link>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Universities; 